import { useState } from "react";
import { useNavigate } from "react-router";
import { BottomCTA, TopNavigation } from "@hamlak/ui";
import { apiFetch, clearLocalSession, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { BirthDateRequest } from "@/api/types";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { DateField, EMPTY_DATE, toIsoDate, type DateParts } from "@/components/DateField";
import { UnderAgeDialog } from "./UnderAgeDialog";
import { OnboardingCloseButton } from "./OnboardingCloseButton";
import "./auth.css";

/**
 * F01-07 생년월일 · F01-08 만 14세 미만. 클라는 형식만 검사하고 나이를 계산하지 않는다(판정은 서버 UTC 날짜).
 * 입력값은 메모리에만 두고 POST 후 버린다. 403 UNDER_AGE → 다이얼로그 → 세션 폐기 → 비회원 홈.
 */
export function BirthDatePage() {
  const navigate = useNavigate();
  const setFlags = useAuthStore((s) => s.setFlags);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [value, setValue] = useState<DateParts>(EMPTY_DATE);
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [underAge, setUnderAge] = useState(false);

  const iso = toIsoDate(value);
  const filled = value.year.length === 4 && value.month.length > 0 && value.day.length > 0;

  const submit = async () => {
    if (!iso) {
      setError(t("birthDate.invalid"));
      return;
    }
    setPending(true);
    setError(undefined);
    try {
      const body: BirthDateRequest = { birthDate: iso };
      await apiFetch<void>(ep.meBirthDate, { method: "POST", body });
      setFlags({ birthDateRequired: false });
      navigate("/profile/new", { replace: true });
    } catch (e) {
      if (isApiError(e) && e.status === 403 && e.code === "UNDER_AGE") {
        setValue(EMPTY_DATE);
        setUnderAge(true);
      } else if (isApiError(e) && e.status === 400) {
        setError(t("birthDate.invalid"));
      } else {
        showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
      }
    } finally {
      setPending(false);
    }
  };

  const leaveAsGuest = async () => {
    setUnderAge(false);
    // 서버가 사용자 행을 삭제했고(웹은 쿠키도 삭제) 토큰은 무효. 로컬 정리만.
    await clearLocalSession();
    await platform.auth.signOut();
    useUiStore.getState().setReturnTo(null);
    navigate("/home", { replace: true });
  };

  return (
    <main className="page auth">
      <TopNavigation title={t("page.birthDate")} left={null} right={<OnboardingCloseButton />} />
      <h1 className="auth__title">{t("birthDate.title")}</h1>
      <p className="auth__description">{t("birthDate.description")}</p>
      <form
        className="auth__form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <DateField value={value} onChange={(v) => (setValue(v), setError(undefined))} error={error} disabled={pending} autoFocus />
        <p className="auth__hint">{t("birthDate.example")}</p>
      </form>
      <BottomCTA primary={{ children: t("birthDate.cta"), disabled: !filled || pending, loading: pending, onClick: () => void submit() }} />
      <UnderAgeDialog open={underAge} onConfirm={() => void leaveAsGuest()} />
    </main>
  );
}
